import { List, ListItemButton, ListItemText, ListProps, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Heater } from '../types'
import { humanizeTimestamp } from '../utils/date'

export interface HeaterDetailsListProps extends Omit<ListProps, 'children'> {
  heater: Heater
  showCreatedAt?: boolean
}

export default function HeaterDetailsList({
  heater,
  showCreatedAt = false,
  ...props
}: HeaterDetailsListProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('heater')

  return (
    <List {...props}>
      <ListItemButton key="catalogProduct.model">
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.name
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noModel')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('model')}
        />
      </ListItemButton>

      <ListItemButton key="catalogProduct.manufacturer">
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.manufacturer
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noManufacturer')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('manufacturer')}
        />
      </ListItemButton>

      <ListItemButton key="catalogProduct.manufacturerProductCode">
        <ListItemText
          primary={
            heater.catalogProduct ? (
              heater.catalogProduct.manufacturerProductCode
            ) : heater.catalogProductId ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : (
              t('noManufacturerProductCode')
            )
          }
          primaryTypographyProps={{
            color: heater.catalogProductId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('manufacturerProductCode')}
        />
      </ListItemButton>

      <ListItemButton key="heatingPower">
        <ListItemText
          primary={`${Math.round(heater.heatingPower)} kW`}
          secondary={t('heatingPower')}
        />
      </ListItemButton>

      <ListItemButton key="serialNumber">
        <ListItemText
          primary={heater.serialNumber ?? t('noSerialNumber')}
          primaryTypographyProps={{
            color: heater.serialNumber ? 'inherit' : 'text.disabled'
          }}
          secondary={t('serialNumber')}
        />
      </ListItemButton>

      <ListItemButton key="owner">
        <ListItemText
          primary={
            heater?.company ? (
              heater.company.name
            ) : heater?.companyId ? (
              <Skeleton variant="text" />
            ) : (
              t('noOwner')
            )
          }
          primaryTypographyProps={{
            color: heater.companyId ? 'inherit' : 'text.disabled'
          }}
          secondary={t('owner')}
        />
      </ListItemButton>

      <ListItemButton key="customerReference">
        <ListItemText
          primary={heater.customerReference ?? t('noCustomerReference')}
          primaryTypographyProps={{
            color: heater.customerReference ? 'inherit' : 'text.disabled'
          }}
          secondary={t('customerReference')}
        />
      </ListItemButton>

      {showCreatedAt && heater?.createdAt && (
        <ListItemButton key="createdAt">
          <ListItemText primary={humanizeTimestamp(heater.createdAt)} secondary={t('createdAt')} />
        </ListItemButton>
      )}
    </List>
  )
}
